import message from "@/utils/globalMessage";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Input, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { api } from "@redux/api";
import TagPill, { hashColor } from "@components/TagPill";

const DROPDOWN_Z = 1100;

const GlobalTag = ({ tableName, recordId, facility, value, onChange }) => {
  const [search, setSearch] = useState("");
  const [internalTags, setInternalTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const shelfRef = useRef(null);

  const isControlled = typeof onChange === "function";
  const selectedTags = isControlled ? (Array.isArray(value) ? value : []) : internalTags;

  const setSelectedTags = useCallback((newTags) => {
    if (isControlled) onChange(newTags);
    setInternalTags(newTags);
  }, [isControlled, onChange]);

  // fetch available tags + record tags
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsFetching(true);
      try {
        const query = facility ? `facility=${facility}` : "";
        const tagsRes = await api.getCustomtags(query);
        if (!cancelled && tagsRes.status === 200) {
          setAllTags((tagsRes.data.results || []).map((t) => ({ label: t.name, value: t.name })));
        }
        if (tableName && recordId) {
          const recordRes = await api.getRecordTagsByEndpoint(tableName, recordId);
          if (!cancelled && recordRes.status === 200 && Array.isArray(recordRes.data.tags)) {
            setInternalTags(recordRes.data.tags);
          }
        }
      } catch (e) { /* silent */ }
        finally { if (!cancelled) setIsFetching(false); }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [tableName, recordId, facility]);

  // save to server
  const saveToServer = useCallback(async (tags) => {
    if (!tableName || !recordId) return;
    setIsSaving(true);
    try { await api.updateRecordTags(tableName, recordId, tags); }
      catch (e) { message.error("Failed to save tags"); }
      finally { setIsSaving(false); }
  }, [tableName, recordId]);

  // suggested tags: existing tags minus already selected, prioritized
  const suggestions = useMemo(() =>
    allTags.filter((t) => !selectedTags.includes(t.value)).slice(0, 8),
    [allTags, selectedTags]);

  // filtered for dropdown
  const available = useMemo(() =>
    allTags.filter((t) =>
      t.label.toLowerCase().includes(search.toLowerCase()) && !selectedTags.includes(t.value)
    ), [allTags, search, selectedTags]);

  const canCreate = search.trim() && !allTags.some((t) => t.label === search.trim());

  const addTag = useCallback((tagName) => {
    const newTags = [...selectedTags, tagName];
    setSelectedTags(newTags);
    setSearch("");
    setActiveIndex(-1);
    // focus back on input after adding from shelf
    setTimeout(() => inputRef.current?.focus(), 0);
    if (tableName && recordId) saveToServer(newTags);
  }, [selectedTags, setSelectedTags, tableName, recordId, saveToServer]);

  const removeTag = useCallback((tagName) => {
    const newTags = selectedTags.filter((t) => t !== tagName);
    setSelectedTags(newTags);
    if (tableName && recordId) saveToServer(newTags);
  }, [selectedTags, setSelectedTags, tableName, recordId, saveToServer]);

  const createAndAdd = useCallback(() => {
    const name = search.trim();
    if (!name) return;
    const newTagData = { name, table_name: tableName, facility: facility || "" };
    api.createCustomtag(tableName, newTagData).then((res) => {
      if (res.status === 200 || res.status === 201) {
        const tag = { label: res.data.name, value: res.data.name };
        setAllTags((prev) => [...prev, tag]);
        addTag(tag.value);
      }
    }).catch(() => message.error("Failed to create tag"));
  }, [search, tableName, facility, addTag]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < available.length) {
        addTag(available[activeIndex].value);
      } else if (canCreate) {
        createAndAdd();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, available.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearch("");
      setActiveIndex(-1);
    } else if (e.key === "Backspace" && !search && selectedTags.length) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  // click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
        setActiveIndex(-1);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // viewport-aware dropdown
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const dd = dropdownRef.current.getBoundingClientRect();
    if (dd.bottom > window.innerHeight - 40) {
      dropdownRef.current.style.bottom = "100%";
      dropdownRef.current.style.top = "auto";
      dropdownRef.current.style.marginTop = "0";
      dropdownRef.current.style.marginBottom = "4px";
    }
  }, [isOpen, available, canCreate]);

  const openEditor = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  if (isFetching) {
    return (
      <div className="inline-flex items-center gap-2 text-xs text-gray-400 py-1">
        <Spin size="small" /> Loading tags...
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {/* main tag area */}
      <div
        onClick={openEditor}
        className="flex flex-wrap items-center gap-1 min-h-[30px] w-full px-2 py-1.5 rounded-lg border border-dashed border-gray-300 hover:border-blue-400 cursor-pointer transition-colors bg-white"
      >
        {selectedTags.map((tag) => (
          <TagPill key={tag} name={tag} onRemove={removeTag} />
        ))}

        {isOpen ? (
          <div className="relative inline-flex items-center" style={{ zIndex: DROPDOWN_Z }}>
            <Input
              ref={inputRef}
              size="small"
              bordered={false}
              style={{ minWidth: 100, padding: 0, fontSize: 13, lineHeight: "22px" }}
              placeholder={selectedTags.length ? "Add tag..." : "Search or create tag..."}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setActiveIndex(-1); }}
              onKeyDown={handleKeyDown}
            />
            <div
              ref={dropdownRef}
              className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
              style={{ minWidth: 220, maxHeight: 260, overflowY: "auto", zIndex: DROPDOWN_Z }}
            >
              {!search.trim() && !available.length && (
                <div className="px-3 py-2 text-xs text-gray-400">Type to search or create a new tag</div>
              )}

              {canCreate && (
                <div
                  className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-blue-50 text-sm text-blue-600 transition-colors"
                  onClick={createAndAdd}
                >
                  <PlusOutlined style={{ fontSize: 12 }} />
                  <span>Create <b>"{search.trim()}"</b></span>
                </div>
              )}

              {canCreate && available.length > 0 && (
                <div className="px-3 py-0.5 text-[10px] text-gray-400 uppercase tracking-wide">Existing tags</div>
              )}

              {available.map((opt, i) => {
                const c = hashColor(opt.value);
                return (
                  <div
                    key={opt.value}
                    className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm transition-colors ${i === activeIndex ? "bg-gray-100" : "hover:bg-gray-50"}`}
                    onClick={() => addTag(opt.value)}
                    onMouseEnter={() => setActiveIndex(i)}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.dot }} />
                    <span>{opt.label}</span>
                  </div>
                );
              })}

              {!canCreate && !available.length && search.trim() && (
                <div className="px-3 py-2 text-xs text-gray-400">No matching tags</div>
              )}
            </div>
          </div>
        ) : (
          !selectedTags.length && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400 select-none">
              <PlusOutlined style={{ fontSize: 10 }} />
              Add tags
            </span>
          )
        )}
      </div>

      {/* suggestions shelf — Evernote-style quick-add */}
      {suggestions.length > 0 && (
        <div ref={shelfRef} className="flex flex-wrap items-center gap-1 mt-2">
          <span className="text-[10px] text-gray-400 uppercase tracking-wide mr-1 select-none">Suggestions</span>
          {suggestions.map((t) => (
            <button
              key={t.value}
              onClick={(e) => { e.stopPropagation(); addTag(t.value); }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 text-xs text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer bg-white"
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: hashColor(t.value).dot }} />
              {t.label}
              <PlusOutlined style={{ fontSize: 8 }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalTag;
